<template>
  <transition-group name="help-text" tag="div" id="help-text">
    <div :key="helpText" v-html="helpText" :class="helpTextClass"></div>
  </transition-group>
</template>

<script>
import { NUM_TO_STR } from "../types.js";
const intersectionSetFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});
export default {
  name: "SequenceHelp",
  data() {
    return {
      helpText: "",
      status: null,
    };
  },
  inject: ["trackEvent"],
  props: {
    intersectionSet: {
      type: Set,
      required: true,
    },
    selectedRowCount: {
      type: Number,
      required: true,
    },
    totalRows: {
      type: Number,
      required: true,
    },
    tooManyClicks: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.setHelpText();
  },
  watch: {
    selectedRowCount() {
      this.setHelpText();
    },
    tooManyClicks(value) {
      this.setHelpText(value);
    },
  },
  computed: {
    helpTextClass() {
      if (this.status == null) {
        return {};
      }
      return this.status === "correct"
        ? { correct: true }
        : { incorrect: true };
    },
    intersectionSetStr() {
      if (this.intersectionSet === false) {
        return "";
      }
      return intersectionSetFormatter.format(
        [...this.intersectionSet].sort((a, b) => a - b).map(String)
      );
    },
  },
  methods: {
    setHelpText(tooManyClicks = false) {
      if (tooManyClicks) {
        this.helpText = "You can only select three sequences.";
        this.status = "incorrect";
        return;
      }
      if (this.selectedRowCount === 0) {
        this.status = null;
        this.helpText =
          "Tap at least two rows to see if you are on the right track.";
      } else if (this.selectedRowCount === 1) {
        this.helpText =
          "Select one more sequence to see which numbers appear multiple times.";
      } else if (this.selectedRowCount === 2) {
        if (this.intersectionSet.size === 0) {
          this.helpText = `<strong>Zero numbers </strong> 
         appear in both selected sequences. These sequences <strong>cannot both</strong> be part of the solution.`;
        } else if (this.intersectionSet.size > 1) {
          this.helpText = `<strong>${
            NUM_TO_STR[this.intersectionSet.size]
          } numbers </strong> (${
            this.intersectionSetStr
          }) appear in both selected sequences. These sequences <strong>cannot both</strong> be part of the solution.`;
        } else {
          this.helpText = `Only one number appears more than once, these <strong>could</strong> be part of the solution. Look for another sequence with the number <strong>${this.intersectionSetStr}</strong> to try and solve.`;
        }
      } else {
        if (this.intersectionSet.size !== 1) {
          this.helpText = `<strong>${
            NUM_TO_STR[this.intersectionSet.size]
          } numbers </strong> (${
            this.intersectionSetStr
          }) appear multiple times in the selected sequences. These three sequences <strong>are not</strong> the solution.`;
        } else {
          this.helpText = `You've found the solution! Only <strong>${this.intersectionSetStr}</strong> is repeated across all the selected sequences. <strong>${this.intersectionSetStr}</strong> is the <strong class="knot-color">knot</strong>.`;
        }
      }
      if (this.selectedRowCount <= 1) {
        this.status = null;
      } else if (this.intersectionSet.size !== 1) {
        this.status = "incorrect";
      } else {
        this.status = "correct";
      }
    },
  },
};
</script>

<style scoped>
* {
  font-family: "Fira Sans", system-ui;
}
.incorrect,
.incorrect :deep(strong) {
  color: var(--red-herring-color) !important;
}

#help-text {
  text-align: center;
  position: relative;
  min-height: 80px;
  display: flex;
}

#help-text :deep(div) {
  font-family: "Roboto Condensed", system-ui;
  font-size: 1.125em;
}
#help-text > div {
  position: absolute;
  top: 50%;
  width: 100%;
  transform: translateY(-50%);
}
#help-text div::first-letter {
  text-transform: uppercase;
}

.help-text-enter-active,
.help-text-leave-active {
  transition: all 0.25s cubic-bezier(0.94, 0.77, 0.57, 1.25);
}
.help-text-enter-from,
.help-text-leave-to {
  opacity: 0;
}
</style>